// Import the RTK Query methods from the React-specific entry point
import { CastingCompaniesQueryApi } from '@lib/api/endpoints';
import { logIfDevelopment } from '@lib/utils/logging';
import { mapErrorFromMessage, mapErrorFromResponse } from '@lib/utils/methods';
import {
  BaseQueryApi,
  BaseQueryExtraOptions,
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

type QueryFn = (
  arg?,
  api?: BaseQueryApi,
  extraOptions?: BaseQueryExtraOptions<BaseQueryFn>,
  baseQuery?: BaseQueryFn
) => Promise<any>;

const getAllCompaniesHandler: QueryFn = (arg) => {
  if (!arg) {
    return new Promise((resolve, reject) => {
      reject();
    }).catch((err) => {
      return mapErrorFromMessage('No ID provided');
    });
  }
  return CastingCompaniesQueryApi.getAllCompanies()
    .then((response) => {
      return { data: response };
    })
    .catch((e) => {
      logIfDevelopment(e);
      return { error: 'Error when getting all companies' };
    });
};

const getSubdomainAvailabilityHandler: QueryFn = async (subdomain) => {
  if (!subdomain) {
    return { error: mapErrorFromMessage('No subdomain provided') };
  }
  try {
    const response = await CastingCompaniesQueryApi.checkCompanySubdomainIsAvailable(subdomain);
    // Ensure response has is_available property
    if (response && typeof response.is_available === 'boolean') {
      return { data: response.is_available };
    } else {
      return { error: 'Invalid response format from subdomain availability API' };
    }
  } catch (e) {
    logIfDevelopment(e);
    return { error: 'Error when checking subdomain availability' };
  }
};


// Define our single API slice object
const companiesApiSlice = createApi({
  reducerPath: 'companies-api',
  baseQuery: fetchBaseQuery({ baseUrl: process.env.NEXT_PUBLIC_API_BASE_PATH }),
  tagTypes: ['companies'],
  endpoints: (builder) => ({
    // GET
    getAllCompanies: builder.query<CompanyWithSettings[], number>({
      queryFn: getAllCompaniesHandler,
      providesTags: ['companies'],
    }),
    getSubdomainAvailability: builder.query<boolean, string>({
      queryFn: getSubdomainAvailabilityHandler,
      providesTags: ['companies'],
      keepUnusedDataFor: 0, // Reset cache after use
    }),
  }),
});

export const { 
  useGetAllCompaniesQuery, 
  useGetSubdomainAvailabilityQuery,
  useLazyGetSubdomainAvailabilityQuery,
  reducer 
} = companiesApiSlice; // hook + reducer export

export { companiesApiSlice }; // slice export
